"use client";

import {useTranslation} from "react-i18next";
import {humanizeToolField} from "@repo/ui/gailgpt/tool-presentation";
import ProposalFieldLabel from "./ProposalFieldLabel";
import {actionFieldLabel} from "./useCaseSnapshot";
import {occurrenceKeys, valueKey} from "./snapshotKeys";

/** Known enum values get labels; anything else is shown exactly as the API sent it. */
const VALUE_LABELS: Record<string, Record<string, string>> = {
   type: {default: "typeDefault", text_message: "typeTextMessage"},
   status: {active: "statusActive", archived: "statusArchived"},
};

interface FrozenValueProps {
   value: unknown;
   /** The property holding this value, which decides how it is labelled. */
   field?: string;
   scriptNames?: ReadonlyMap<string, string>;
   humanizeLabels?: boolean;
}

/** Render API values without normalization. Array position is part of the proposal. */
export default function FrozenValue({value, field, scriptNames, humanizeLabels = false}: FrozenValueProps) {
   const {t, i18n} = useTranslation("ask");
   const muted = (key: string) => <span className="italic text-500">{t(`toolApproval.${key}`)}</span>;
   if (value === undefined) return muted("absentValue");
   if (value === null) return muted("nullValue");
   if (value === "") return muted("emptyValue");
   if (typeof value === "boolean") return <>{t(`toolApproval.${value ? "trueValue" : "falseValue"}`)}</>;
   if (Array.isArray(value)) return value.length ? <ol className="list-decimal space-y-3 pl-5 marker:font-semibold marker:text-500">
      {occurrenceKeys(value, valueKey).map(({key, item}) => <li key={key} className="pl-1">
         <FrozenValue value={item} field={field} scriptNames={scriptNames} humanizeLabels={humanizeLabels} />
      </li>)}
   </ol> : muted("emptyList");
   if (typeof value === "object" && !Object.keys(value).length) return muted("emptyValue");
   if (typeof value === "object") return <dl className="space-y-2">{Object.entries(value).map(([key, item]) => {
      const translated = `toolApproval.fields.${key}`;
      const humanizedLabel = i18n.exists(translated, {ns: "ask"}) ? t(translated) : humanizeToolField(key);
      return <div key={key}>
         <dt className="text-xs font-medium text-500">{humanizeLabels
            ? humanizedLabel
            : <ProposalFieldLabel field={field ? key : actionFieldLabel(key)} />}</dt>
         <dd className="whitespace-pre-wrap [overflow-wrap:anywhere]"><FrozenValue value={item} field={key} scriptNames={scriptNames} humanizeLabels={humanizeLabels} /></dd>
      </div>;
   })}</dl>;
   const text = String(value);
   if (field === "textScriptId") {
      const name = scriptNames?.get(text.toLowerCase());
      return <span title={text} className="[overflow-wrap:anywhere]">{name ?? text}</span>;
   }
   const labelKey = field ? VALUE_LABELS[field]?.[text] : undefined;
   if (labelKey) return <>{t(`toolApproval.${labelKey}`)}</>;
   return <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{text}</span>;
}
